import { createSlice } from '@reduxjs/toolkit';
import { resolveModules } from '../../data/roleAccess';
import { DEFAULT_ATTENDANCE_MODE } from '../../data/attendanceModes';

const KEY = 'ara_auth';

const EMPTY = {
    token: null,
    refreshToken: null,
    expiresOn: null,
    refreshTokenExpiresOn: null,
    isAuthenticated: false,
    user: null,
    modules: [],
    attendanceMode: DEFAULT_ATTENDANCE_MODE,
    remember: true,

    userName: '',
    email: '',
    role: '',
    organisation: '',
};

const isExpired = (expiresOn) => {
    if (!expiresOn) return false;
    const t = new Date(expiresOn).getTime();
    return Number.isFinite(t) && t <= Date.now();
};

const readStored = () => {
    try {
        const raw = localStorage.getItem(KEY) || sessionStorage.getItem(KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const accessDead = isExpired(parsed.expiresOn);
        const refreshDead = !parsed.refreshToken || isExpired(parsed.refreshTokenExpiresOn);
        if (accessDead && refreshDead) {
            localStorage.removeItem(KEY);
            sessionStorage.removeItem(KEY);
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

const persist = (state) => {
    try {
        const target = state.remember ? localStorage : sessionStorage;
        const other = state.remember ? sessionStorage : localStorage;
        other.removeItem(KEY);
        target.setItem(KEY, JSON.stringify(state));
    } catch { }
};

const clearStored = () => {
    try {
        localStorage.removeItem(KEY);
        sessionStorage.removeItem(KEY);
    } catch { }
};

const initialState = readStored() || EMPTY;

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        loginSuccess: (state, action) => {
            const { token, expiresOn, refreshToken, refreshTokenExpiresOn, user, modules, attendanceMode, remember } = action.payload;
            const next = {
                token,
                refreshToken: refreshToken || null,
                expiresOn: expiresOn || null,
                refreshTokenExpiresOn: refreshTokenExpiresOn || null,
                isAuthenticated: true,
                user: user || null,
                modules: Array.isArray(modules) ? modules : [],
                // The API may carry this top-level or on the user; take either.
                attendanceMode: attendanceMode || user?.attendanceMode || DEFAULT_ATTENDANCE_MODE,
                remember: remember !== false,

                userName: user?.name || user?.loginId || '',
                email: user?.email || user?.loginId || '',
                role: user?.role || '',
                organisation: 'ARA HumanSync',
            };
            persist(next);
            return next;
        },

        tokenRefreshed: (state, action) => {
            const { token, expiresOn, refreshToken, refreshTokenExpiresOn } = action.payload;
            state.token = token;
            if (expiresOn) state.expiresOn = expiresOn;
            if (refreshToken) state.refreshToken = refreshToken;
            if (refreshTokenExpiresOn) state.refreshTokenExpiresOn = refreshTokenExpiresOn;
            persist(state);
        },

        logout: () => {
            clearStored();
            return { ...EMPTY };
        },

        profileUpdated: (state, action) => {
            const { name, phone } = action.payload || {};
            if (name != null) {
                state.userName = name;
                if (state.user) state.user.name = name;
            }
            if (phone !== undefined && state.user) {
                state.user.phone = phone;
            }
            persist(state);
        },
    },
});

export const { loginSuccess, tokenRefreshed, logout, profileUpdated } = authSlice.actions;

export const selectAuth = (s) => s.auth;
export const selectToken = (s) => s.auth.token;
export const selectUser = (s) => s.auth.user;
export const selectModules = (s) => s.auth.modules;
export const selectIsAuthenticated = (s) => s.auth.isAuthenticated;

export const selectEffectiveModules = (s) => resolveModules(s.auth.user, s.auth.modules);

export const USER_TYPE = { MASTER_ADMIN: 1, ADMIN: 2, EMPLOYEE: 3 };

export const selectUserTypeId = (s) => s.auth.user?.userTypeId ?? null;
export const selectIsMasterAdmin = (s) => s.auth.user?.userTypeId === USER_TYPE.MASTER_ADMIN || s.auth.user?.isMasterAdmin === true;
export const selectIsAdmin = (s) => s.auth.user?.userTypeId === USER_TYPE.ADMIN;
export const selectIsEmployee = (s) => s.auth.user?.userTypeId === USER_TYPE.EMPLOYEE;

export const selectAttendanceMode = (s) =>
    s.auth.attendanceMode || s.auth.user?.attendanceMode || DEFAULT_ATTENDANCE_MODE;

// The exact numeric-id field name on the backend user object is unconfirmed —
// try the likely candidates in order. Confirm against a real login response
// and drop the fallbacks once it's known for certain.
export const selectUserId = (s) => {
    const u = s.auth.user;
    if (!u) return null;
    const id = u.id ?? u.userId ?? u.Id;
    return id == null ? null : id;
};

export const selectUserEntityId = (s) => s.auth.user?.entityId ?? null;
export const selectUserEntityName = (s) => s.auth.user?.entityName ?? null;

export const selectCanViewAllEntities = (s) => selectIsMasterAdmin(s);

export const selectHasModule = (key) => (s) =>
    !s.auth.modules?.length || s.auth.modules.includes(key);

export const getStoredToken = () => readStored()?.token || null;
export const getStoredAuth = () => readStored();

export default authSlice.reducer;
